// Figma: https://www.figma.com/design/S3yxsM3h4CuTy6ZZjxpedQ/NEW-TSAB-Customer-App?node-id=19307-115632
// The app's `returnTripBookingSheet` (docs/RETURNTRIP.md) as a view in the booking module.
//
// Everything is prefilled from the original booking (RETURN_TRIP_BOOKING in BookingModule);
// the user only picks a pickup time — or flight number, landing time and luggage when the
// return pickup is an airport — and books. Availability, price, price type and payment
// validation come from the module's Fetcher exactly like the regular flow, and the booking
// itself goes through usePlaceBooking, shared with ConfirmBooking.
//
// Web interpretation of the sheet: laid out like the booking summary rather than the app's
// sheet. FormContent holds the title and the summary rows — the time/flight inputs in the time
// sheet. FormContent (right column in fullscreen) holds the title and the summary rows: trip,
// payment with the price, car type, company, reference, passenger and message, with edit
// pencils (trip parts leave for the normal flow with everything kept; the rest edit in place;
// payment is locked). contentBottom (left column) holds the time/flight inputs and the single
// "Book & pay" button in the button drawer. The module's reset button is Close.
import { useContext, useEffect, useRef } from 'react'
import { useFormikContext, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { addDays, clamp, setHours, setMinutes, setSeconds } from 'date-fns'
import classnames from 'classnames/bind'
import { StepperContext } from '~/components/ui/ViewStepper'
import {
  FormContent, FormHeader, ButtonDrawer, InputField, CheckboxField, DatePicker, FormError,
  Notifications, NumberStepper, TripLocations, SelectedPayment, SelectedCar, PassengerInfo,
  MessageToDriver, TripType, BookingSummaryField, EditField, Icon
} from '~/components'
import PriceInfoModal from '~/components/ui/PriceInfoModal'
import { wrapWithValidation } from '~/utils'
import { showModal, showContentModal } from '~/lib/modal'
import { hasInformationTag, getOptionText, getAvailableVehicleOptions, getPaymentDisplay, filterOptions, normalizePriceType, getPaymentMethodKey } from '~/utils/data'
import { getOptions } from '~/utils/format'
import usePlaceBooking from '../usePlaceBooking'
import ConfirmBooking from '../ConfirmBooking'
import { getMinDate, getMaxDate } from '../SelectTime'
import * as common from '~/config/common.module.css'
import * as timeStyles from '../SelectTime/index.module.css'
import * as MotionElement from '~/components/ui/MotionElement'
import { useTranslation } from 'react-i18next'
import i18next from '~/i18n'

const cx = classnames.bind(common)

const ReturnTripBooking = ({ fullscreen }) => {
  const { t } = useTranslation()
  const formik = useFormikContext()
  const { values, errors, touched, setFieldValue } = formik
  const { prev } = useContext(StepperContext)

  const original = values.returnTripBooking
  const departureIsAirport = hasInformationTag(values, 'FLIGHT_NBR')
  const requiresPassengerCount = hasInformationTag(values, 'TRAVELER_COUNT_REQUIRED')
  const timeSelected = values.pickupTime !== '' && values.pickupTime !== null && values.pickupTime !== undefined

  const prebookTripRules = values.availability?.prebook?.tripRules
  const minDate = getMinDate(prebookTripRules)
  const maxDate = getMaxDate(values)
  const clampDate = date => clamp(date, { start: minDate, end: maxDate })

  const availabilities = values.availability?.prebook?.availabilities || []
  const maxPassengers = availabilities.reduce((max, a) => {
    if (a.availableAt === 'NOT_AVAILABLE' || !a.maxTravelers) return max
    return Math.max(max, a.maxTravelers)
  }, 0) || 50

  // "Edit", and the blocking-problem dialogs: leave the sheet for the normal flow with
  // everything the customer entered intact (addresses, car type, add-ons, time, flight info,
  // originalBookingReference), starting from the first step like the app's start page.
  const leaveForNormalFlow = () => prev('locations')

  const { placeBooking, isLoading, waitingForPoll } = usePlaceBooking({
    onNotAvailable: leaveForNormalFlow,
    // The picked time has passed by the time we booked: clear it so the field asks again.
    onDatetimeBeforeNow: () => setFieldValue('pickupTime', '', false),
  })
  const book = wrapWithValidation(placeBooking, formik)

  // Price (same lookup as BookingSummary)
  const fixedPrice = values.autoValues.fixedPrice !== null ? values.autoValues.fixedPrice : values.fixedPrice
  const serviceType = values.autoValues.serviceType !== null ? values.autoValues.serviceType : values.serviceType
  const actualServiceType = serviceType === 'estate' ? 'taxi' : serviceType
  const price = values?.priceEstimate?.prices?.find(p =>
    p.serviceType === actualServiceType &&
    (values.selectedProviderId ? p.providerId === values.selectedProviderId : true)
  )
  const showPrice = timeSelected && !!fixedPrice && !!price
  const priceLoading = timeSelected && (values.priceIsLoading || values.availabilityIsLoading)
  const { icon: paymentIcon, title: paymentTitle, subtitle: paymentSubtitle } = getPaymentDisplay(values)

  const openPriceInfo = showPrice
    ? () => showContentModal((close) => <PriceInfoModal price={price} onClose={close} />)
    : null

  // Prefilled details
  const companyName = values.company?.name
  const reference = values.accountReference?.trim()

  // Prebook availability (and its earliestPrebookingDateTime) only arrives once a time is set.
  // Snap a picked time that turns out to be before the API minimum, once, like SelectTime.
  useEffect(() => {
    const earliest = prebookTripRules?.earliestPrebookingDateTime
    if (!earliest || !timeSelected) return
    const newMinDate = getMinDate(prebookTripRules)
    if (!values.pickupTimeInitialized) {
      setFieldValue('pickupTimeInitialized', true, false)
      if (new Date(values.pickupTime) < newMinDate) setFieldValue('pickupTime', newMinDate, false)
    } else if (new Date(values.pickupTime) < new Date(earliest)) {
      setFieldValue('pickupTime', newMinDate, false)
    }
  }, [prebookTripRules?.earliestPrebookingDateTime])

  // The prebook availability for the picked time, once it is in and the time is one the
  // backend accepts. The date picker commits "now" when it is opened, and the backend answers
  // NOT_AVAILABLE for every car type until the snap effect above has moved the time to the
  // earliest prebooking time, so nothing below may judge that first, too-early answer.
  const prebook = values.availability?.prebook
  const earliest = prebookTripRules?.earliestPrebookingDateTime
  const snapPending = !!earliest && new Date(values.pickupTime) < getMinDate(prebookTripRules)
  // The time each prebook answer was fetched for. The Fetcher refetches on a time change, but
  // in the render where the time changed the previous answer is still in the form.
  // Recorded during render (not in an effect) so the render that brings the answer sees it.
  const lastPrebookRef = useRef(null)
  const fetchedForRef = useRef(null)
  if (prebook !== lastPrebookRef.current) {
    lastPrebookRef.current = prebook
    fetchedForRef.current = prebook ? new Date(values.pickupTime).getTime() : null
  }
  const prebookIsForPickupTime = !!prebook && fetchedForRef.current === new Date(values.pickupTime).getTime()
  const prebookSettled = timeSelected && !values.availabilityIsLoading && prebookIsForPickupTime && !snapPending

  // Payment is locked to the original trip, so it is checked against the price types of the
  // prebook availability itself rather than values.priceType, which the Fetcher derives one
  // render later (and which still belongs to the direct availability fetched when the sheet
  // opened). Fixed price vs taximeter is left to the Fetcher like in the regular flow.
  const originalPayment = values.returnTripPayment
  const prebookPriceTypes = getPrebookPriceTypes(prebook, values.selectedProviderId)
  const originalPaymentAllowed = isPaymentAllowed(originalPayment, prebookPriceTypes, values.company?.id)

  // The Fetcher may have cleared the carried-over payment while the direct availability from
  // the sheet's opening was the current one (its price types can exclude e.g. cards), and it
  // only restores the *preferred* payment. Put the original trip's payment back once the
  // prebook availability allows it.
  useEffect(() => {
    if (!prebookSettled || !originalPaymentAllowed) return
    if (values.payment === originalPayment.payment) return
    setFieldValue('payment', originalPayment.payment, false)
    setFieldValue('account', originalPayment.account, false)
  }, [prebookSettled, originalPaymentAllowed, values.payment, values.priceType])

  // Problems that can't be fixed inside the sheet (car type, add-ons and payment method are
  // locked to the original trip). The user is offered to continue in the normal flow. A time
  // that is merely DELAYED is not blocking: the autoValues handle it like the regular flow.
  const problemShownRef = useRef(false)
  const originalOptions = filterOptions(original?.vehicleOptions)
  useEffect(() => {
    if (!prebookSettled || problemShownRef.current) return

    const usable = (prebook.availabilities || []).filter(a => a.availableAt !== 'NOT_AVAILABLE')
    const wanted = values.serviceType === 'estate' ? 'taxi' : values.serviceType
    const available = getAvailableVehicleOptions(values)

    let problem = null
    if (usable.length === 0 || prebookPriceTypes.length === 0) problem = 'return-trip-error-not-available'
    else if (!usable.some(a => a.serviceType === wanted)) problem = 'return-trip-error-car-type'
    else if (!originalOptions.every(option => available.includes(option))) problem = 'return-trip-error-extras'
    else if (!originalPaymentAllowed) problem = 'return-trip-error-payment-method'
    if (!problem) return

    problemShownRef.current = true
    showModal(t(problem), [{ title: t('button-continue'), callback: leaveForNormalFlow }], { title: t('return-trip-error-title') })
  }, [prebook, prebookSettled, originalPaymentAllowed])

  const timeError = !!(touched.pickupTime && errors.pickupTime)
  const bookTitle = showPrice && !priceLoading
    ? t('button-book-and-pay', { price: price.finalPrice })
    : t('button-book-taxi')
  const buttons = [
    {
      func: book,
      title: bookTitle,
      isLoading: isLoading || waitingForPoll,
      disabled: isLoading || waitingForPoll || priceLoading,
    },
  ]

  // Edit pencils. Trip parts (addresses, car type, add-ons) are edited in the normal flow with
  // everything kept, like the blocking-problem dialogs; name, phone, message and reference are
  // edited in place with the same modals as the booking summary.
  const editLocations = leaveForNormalFlow
  const editCar = () => prev(timeSelected ? 'how' : 'time')
  const saveField = (id, value) => setFieldValue(id, value, false)
  const validateName = value => (String(value ?? '').length < 2 ? t('validation-need-name') : undefined)
  const validatePhone = value => {
    if (!String(value ?? '').match(/^\+?\d+$/)) return t('validation-only-numbers')
    if (String(value).length < 2) return t('validation-need-phone')
    return undefined
  }
  const openEditPassenger = () => showContentModal((close) => (
    <EditField validate formValues={values} onFieldSave={saveField} title={t('header-whos-travel')} onClose={close} fields={[
      { id: 'name', title: t('placeholder-name'), validate: validateName },
      { id: 'phone', title: t('placeholder-phone'), validate: validatePhone },
    ]} />
  ))
  const openEditMessage = () => showContentModal((close) => (
    <EditField formValues={values} onFieldSave={saveField} title={t('title-message-to-driver')} onClose={close} fields={[
      { id: 'generalComment', title: t('title-message-to-driver') },
    ]} />
  ))
  const openEditReference = () => showContentModal((close) => (
    <EditField formValues={values} onFieldSave={saveField} title={t('placeholder-reference')} onClose={close} fields={[
      { id: 'accountReference', title: t('placeholder-reference') },
    ]} />
  ))
  const showReference = !!values.company || values.account?.type === 'TSAB' || values.account?.type === 'TCA'

  const passengerCountField = requiresPassengerCount && (
    <div>
      <p className={common.label}>{t('header-passengers')}</p>
      <div className="flex items-center gap-3 py-2">
        <span className="flex-1 text-[17px] font-medium tracking-[-0.34px] leading-[22px] text-on-surface">{t('label-group-size')}</span>
        <NumberStepper
          value={values.passengerCount}
          min={0}
          max={maxPassengers}
          onChange={v => setFieldValue('passengerCount', v)}
        />
      </div>
      <p className={common.subtitle}>{t('info-passenger-count')}</p>
    </div>
  )

  return (
    <div className={cx(common.widgetWrapper, { fullscreen, isLoading })}>
      <FormContent>
        <FormHeader>{t('return-trip-title')}</FormHeader>
        <Notifications fullscreen={fullscreen} showBothBookingTypes />

        <MotionElement.div className={cx(common.contentFull, 'items-start')}>
          <div>
            <div className="flex items-start">
              <div className="flex-1 min-w-0">
                <TripLocations
                  departure={values.departure}
                  destination={values.destination}
                  stops={values.intermediateStopLocations}
                />
              </div>
              <button type="button" onClick={editLocations} className="shrink-0 p-2 -mr-2 mt-2" aria-label={t('button-change')}>
                <Icon id="pen" />
              </button>
            </div>
          </div>

          <div>
            <SelectedPayment
              icon={paymentIcon}
              title={paymentTitle || t('title-payment')}
              subtitle={paymentSubtitle}
              priceLabel={timeSelected ? (showPrice ? t('label-fixed-price') : (fixedPrice ? null : t('label-taxameter'))) : null}
              price={showPrice ? price : null}
              priceLoading={priceLoading}
              voucherPills={[]}
              onInfo={openPriceInfo}
            />
            <ErrorMessage name="payment" component={FormError} />
            <ErrorMessage name="accountReference" component={FormError} />
          </div>

          <div>
            <SelectedCar
              serviceType={values.serviceType}
              options={getOptions(values).map(getOptionText)}
              onEdit={editCar}
            />
          </div>

          {values.company && (
            <div>
              <TripType type="company" name={companyName} />
            </div>
          )}

          {showReference && (
            <div>
              <BookingSummaryField
                title={t('placeholder-reference')}
                value={reference || t('validation-need-reference')}
                icon="Reference"
                changeToggle={openEditReference}
                required
                empty={!reference}
              />
            </div>
          )}

          {(values.name || values.phone) && (
            <div>
              <PassengerInfo name={values.name} phone={values.phone} onEdit={openEditPassenger} />
            </div>
          )}

          <div>
            <MessageToDriver
              message={values.generalComment || 'Hjälp föraren att hitta dig.'}
              onEdit={openEditMessage}
            />
          </div>
        </MotionElement.div>
      </FormContent>

      <div className={common.contentBottom}>
        <FormHeader onlyFullscreen>{departureIsAirport ? t('header-when-does-your-flight-land') : t('header-when-do-you-want-to-go')}</FormHeader>
        <MotionElement.div className={common.content}>
          <div className={common.group}>
            {departureIsAirport ? (
              <>
                <div>
                  <p className={common.label}>{t('section-flight-number')}</p>
                  <Field
                    name="flightNumber"
                    placeholder={t('placeholder-flight-number')}
                    component={InputField}
                    onChange={(e) => {
                      const value = e.target.value || ""
                      setFieldValue('flightNumber', value.toUpperCase())
                    }}
                  />
                </div>

                <div>
                  <p className={common.label}>{t('section-landing-time')}</p>
                  <div className={timeStyles.datepicker}>
                    <Field
                      name="pickupTime"
                      minDate={minDate}
                      maxDate={maxDate}
                      clampDate={clampDate}
                      component={DatePicker}
                      showTimeInput
                      commitOnOpen
                      placeholder={t('placeholder-select-landing-time')}
                      error={timeError}
                      isAirport
                    />
                  </div>
                  <ErrorMessage name="pickupTime" component={FormError} />
                  <p className={common.subtitle}>{t('return-trip-subtitle-flight')}</p>
                </div>

                {passengerCountField}

                <div>
                  <p className={common.label}>{t('section-luggage')}</p>
                  <Field
                    name="checkedInBaggage"
                    title={t('title-baggage')}
                    tabIndex="0"
                    alternateBackground={fullscreen}
                    component={CheckboxField}
                    padding="py-2"
                    icon="Luggage"
                  />
                  <p className={common.subtitle}>{t('info-baggage')}</p>
                </div>
              </>
            ) : (
              <>
                <div>
                  <p className={common.label}>{t('label-pickup-time')}</p>
                  <div className={timeStyles.datepicker}>
                    <Field
                      name="pickupTime"
                      minDate={minDate}
                      maxDate={maxDate}
                      clampDate={clampDate}
                      component={DatePicker}
                      showTimeInput
                      commitOnOpen
                      placeholder={t('placeholder-select-pickup-time')}
                      error={timeError}
                    />
                  </div>
                  <ErrorMessage name="pickupTime" component={FormError} />
                </div>

                {passengerCountField}
              </>
            )}
          </div>
        </MotionElement.div>
      </div>

      <ButtonDrawer buttons={buttons} fullscreen={fullscreen} />
    </div>
  )
}

// Price types of a (normalized) availability, for the selected provider in multiple-partner
// mode, otherwise the aggregated ones. Same lookup as the Fetcher's getPriceTypes.
const getPrebookPriceTypes = (availability, selectedProviderId) => {
  if (!availability) return []
  const providers = availability.servicesByProvider
  const selected = Array.isArray(providers) && selectedProviderId
    ? providers.find(p => p.providerData?.providerId === selectedProviderId)
    : null
  const priceTypes = selected
    ? selected.priceAndPaymentRules?.priceTypes
    : availability.priceAndPaymentRules?.priceTypes
  return (priceTypes || []).map(normalizePriceType)
}

// Whether the carried-over payment can be used with any of the price types (same rules as
// the Fetcher: method must be offered, and a company-restricted account needs that company).
const isPaymentAllowed = (originalPayment, priceTypes, companyId) => {
  if (!originalPayment) return false
  const { payment, account } = originalPayment
  const method = getPaymentMethodKey(payment, account)
  if (!method) return false
  if (account?.restrictedToCompanyId && account.restrictedToCompanyId !== companyId) return false
  return priceTypes.some(pt => pt.paymentMethods?.includes(method))
}

const FLIGHT_NUMBER_PATTERN = /^([0-9A-Z][0-9A-Z])[A-Z]?[0-9]{1,4}[A-Z]?$/

// Time + flight rules from SelectTime (with the return trip's own "pick a time" copy),
// plus ConfirmBooking's payment/reference/traveler rules.
ReturnTripBooking.validationSchema = Yup.object({
  pickupTime: Yup.date()
    .when(['departure', 'availability'], (departure, availability) => {
      const isAirport = departure?.locationInformation?.some(info => info?.tag === 'FLIGHT_NBR')
      const message = isAirport ? i18next.t('validation-need-landing-time') : i18next.t('validation-need-pickup-time')
      const hasAvailability = availability?.prebook !== undefined && availability?.prebook !== false
      const maxDate = hasAvailability ? getMaxDate({ availability }) : addDays(setHours(setMinutes(setSeconds(new Date(), 0), 59), 23), 365)
      const earliest = availability?.prebook?.tripRules?.earliestPrebookingDateTime
      const schema = Yup.date()
        .typeError(message)
        .required(message)
        .max(maxDate, i18next.t('validation-date-too-far-ahead'))
      return earliest
        ? schema.min(getMinDate(availability.prebook.tripRules), i18next.t('validation-date-not-valid'))
        : schema
    }),

  flightNumber: Yup.string()
    .when('departure', (departure) => {
      if (departure?.locationInformation?.some(info => info?.tag === 'FLIGHT_NBR')) {
        return Yup.string().matches(FLIGHT_NUMBER_PATTERN, i18next.t('validation-need-flight-number')).required(i18next.t('validation-need-flight-number'))
      }
      return Yup.string()
    }),

  passengerCount: Yup.number()
    .when('departure', (departure) => {
      const required = departure?.locationInformation?.some(info => info?.tag === 'TRAVELER_COUNT_REQUIRED')
      return required
        ? Yup.number().min(1, i18next.t('validation-need-passenger-count'))
        : Yup.number()
    }),

  departure: Yup.mixed(),
  availability: Yup.mixed(),
}).concat(ConfirmBooking.validationSchema)

export default ReturnTripBooking
